// Contracts — work.weern.com contractor NOTIFICATIONS (2026-06-24). Manager →
// contractor messages shown on the cabinet homes. Receiving end is live; the
// manager "send" UI is a follow-up. Shapes from migrations/0009.
import { z } from 'zod';

export const WorkNotiTabSchema = z.enum(['', 'photographer', 'gift', 'generator', 'designer']);
export const WorkNotiKindSchema = z.enum(['message', 'alert']);

// A photo attached to an aggregated notification (review results / QA queue):
// signed thumb URL + the machine reason (web renders the localized manifest copy).
export const WorkNotiPhotoSchema = z.object({
  id: z.string(),
  thumb_url: z.string().default(''),
  reason: z.string().default(''),
});
export type WorkNotiPhoto = z.infer<typeof WorkNotiPhotoSchema>;

export const WorkNotificationSchema = z.object({
  id: z.string(),
  tab: WorkNotiTabSchema,        // which home it shows on ('' = any)
  kind: WorkNotiKindSchema,      // message (green) | alert (amber)
  title: z.string(),
  body: z.string(),
  created_at: z.string(),
  read: z.boolean(),
  // ── Aggregated auto-notifications (0025). nkind '' = a plain manager message
  // (render title/body); 'review_result' = QA finished checking (counts+photos);
  // 'qa_pending' = frames awaiting review (QA-audience feed). All defaulted so
  // historical rows and old workers keep parsing.
  nkind: z.string().default(''),
  credited: z.number().int().default(0),   // review_result: frames accepted
  rejected: z.number().int().default(0),   // review_result: frames rejected
  pending: z.number().int().default(0),    // qa_pending: frames awaiting review
  comment: z.string().default(''),         // manager's review comment (if any)
  photos: z.array(WorkNotiPhotoSchema).default([]),  // ≤6 problem/queued frames
});
export type WorkNotification = z.infer<typeof WorkNotificationSchema>;

// GET /api/work/notifications — the caller's notifications (their own + broadcast).
export const WorkNotificationsResSchema = z.object({
  notifications: z.array(WorkNotificationSchema),
});
export type WorkNotificationsRes = z.infer<typeof WorkNotificationsResSchema>;

// POST /api/work/notifications/read — mark the caller's own notifications read.
export const WorkNotifReadReqSchema = z.object({
  ids: z.array(z.string()).min(1).max(60),
});
export type WorkNotifReadReq = z.infer<typeof WorkNotifReadReqSchema>;
export const WorkNotifReadResSchema = z.object({ ok: z.boolean() });
export type WorkNotifReadRes = z.infer<typeof WorkNotifReadResSchema>;

// GET /api/work/manager/qaFeed — the QA-audience feed (debounced "N frames
// awaiting review" aggregates per photographer), newest first.
export const WorkQaFeedItemSchema = WorkNotificationSchema.extend({
  photographer_id: z.string().default(''),
  photographer_name: z.string().default(''),
  photographer_email: z.string().default(''),
});
export type WorkQaFeedItem = z.infer<typeof WorkQaFeedItemSchema>;
export const WorkQaFeedResSchema = z.object({
  items: z.array(WorkQaFeedItemSchema),
});
export type WorkQaFeedRes = z.infer<typeof WorkQaFeedResSchema>;

// GET /api/work/manager/inviteLink — the caller-manager's own short join link
// (auto-created on first call). url = <public site>/?join=<code> — deliberately
// carries NO token and no params soup ("не страшная" link).
export const WorkInviteLinkResSchema = z.object({
  code: z.string(),
  url: z.string(),
  label: z.string(),
});
export type WorkInviteLinkRes = z.infer<typeof WorkInviteLinkResSchema>;

// POST /api/work/manager/notify — a manager creates a notification (the send end,
// ready for the manager cabinet UI). photographer_id '' = broadcast to all.
export const WorkNotifyReqSchema = z.object({
  photographer_id: z.string().trim().default(''),
  tab: WorkNotiTabSchema.default(''),
  kind: WorkNotiKindSchema.default('message'),
  title: z.string().trim().min(1).max(200),
  body: z.string().trim().max(2000).default(''),
});
export type WorkNotifyReq = z.infer<typeof WorkNotifyReqSchema>;

export const WorkNotifyResSchema = z.object({ ok: z.boolean(), id: z.string() });
export type WorkNotifyRes = z.infer<typeof WorkNotifyResSchema>;
